// Scheduled-time helpers for things that only ever happen in Melbourne
// (auction times, inspections). new Date(iso).getHours()/.toLocaleString()
// and new Date(dateTimeString) both use the VIEWER's browser timezone -
// correct for "what time is it for me right now", wrong for "what time does
// this Melbourne auction actually start", which is the same real-world
// instant no matter who's looking at it or from where.
//
// Confirmed live, 2026-08-14: 8/118 Miller Street's auction showed 12:30pm in
// the CRM and 2:30pm on realestate.com.au for the exact same stored instant -
// REA's own conversion (always explicitly Melbourne) was the correct one; the
// CRM's browser-local read/write was off by whatever the viewer's/editor's
// local UTC offset differs from Melbourne's.

export const MELBOURNE_TZ = 'Australia/Melbourne';

const _melbournePartsOf = (date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MELBOURNE_TZ, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  // en-CA gives "24" for midnight instead of "00" - normalise.
  if (parts.hour === '24') parts.hour = '00';
  return parts;
};

// Format a stored (UTC) datetime into the Melbourne YYYY-MM-DD part, for a
// date input's value.
export const formatMelbourneDatePart = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = _melbournePartsOf(d);
  return `${p.year}-${p.month}-${p.day}`;
};

// Format a stored (UTC) datetime into the Melbourne HH:MM part, for a time
// input's value.
export const formatMelbourneTimePart = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const p = _melbournePartsOf(d);
  return `${p.hour}:${p.minute}`;
};

// Format a stored (UTC) datetime for read-only display, always as Melbourne
// wall-clock time regardless of the viewer's own timezone.
export const formatMelbourneDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString([], {
    timeZone: MELBOURNE_TZ,
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const _melbourneOffsetMinutesAt = (roughUtcMillis) => {
  const offsetPart = new Intl.DateTimeFormat('en-US', {
    timeZone: MELBOURNE_TZ, timeZoneName: 'longOffset',
  }).formatToParts(new Date(roughUtcMillis)).find((p) => p.type === 'timeZoneName')?.value || 'GMT+10:00';
  const m = offsetPart.match(/GMT([+-])(\d{2}):(\d{2})/);
  return m ? (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) * 60 + parseInt(m[3], 10)) : 600;
};

// A "YYYY-MM-DD" + "HH:MM" pair the agent typed, meant as Melbourne
// wall-clock time, converted to the correct UTC instant - not whatever the
// browser's own local timezone would otherwise produce from a bare
// (timezone-less) datetime string. Handles DST without hardcoding +10/+11
// (good enough outside the exact instant of the twice-yearly changeover,
// which is never an actual auction/inspection time).
export const melbourneWallTimeToUtcISOString = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const roughUtcMillis = Date.parse(`${dateStr}T${timeStr || '00:00'}:00Z`);
  if (isNaN(roughUtcMillis)) return null;
  const offsetMinutes = _melbourneOffsetMinutesAt(roughUtcMillis);
  return new Date(roughUtcMillis - offsetMinutes * 60000).toISOString();
};

// Same conversion for a native <input type="datetime-local"> value, which
// comes back as one combined "YYYY-MM-DDTHH:MM" string instead of two parts.
export const datetimeLocalToUtcISOString = (value) => {
  if (!value) return null;
  const [dateStr, timeStr] = value.split('T');
  return melbourneWallTimeToUtcISOString(dateStr, timeStr);
};

// Read-only "Sat, 29 Aug 2026" day label in Melbourne time — the fixed-timezone
// replacement for moment(iso).format('ddd, D MMM YYYY'), which renders in the
// viewer's own browser timezone (so a Manila-based agent saw the wrong day near
// midnight and the wrong wall-clock time for a Melbourne open home).
// Read-only "21 Sept 2026" label in Melbourne time, for a table column where the
// weekday formatMelbourneDayLabel adds is noise. Same timezone rule, one place.
export const formatMelbourneShortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: MELBOURNE_TZ,
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(d);
};

export const formatMelbourneDayLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: MELBOURNE_TZ,
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  }).format(d);
};

// Read-only "1:00pm" clock label in Melbourne time — the fixed-timezone
// replacement for moment(iso).format('h:mma'). Accepts an ISO string or a Date,
// so a computed end instant (start + duration) formats through the same helper.
export const formatMelbourneClock = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MELBOURNE_TZ, hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.hour}:${parts.minute}${(parts.dayPeriod || '').toLowerCase()}`;
};

// { hour, minute } of a stored (UTC) datetime as Melbourne wall-clock time,
// parsed from formatMelbourneTimePart's "HH:MM". For laying out calendar event
// positions (row = time of day) without pulling in the viewer's own timezone,
// which would otherwise place a Melbourne inspection at the wrong hour row.
export const melbourneHourMinute = (iso) => {
  const hhmm = formatMelbourneTimePart(iso); // "HH:MM" (24h) or ""
  if (!hhmm) return { hour: 0, minute: 0 };
  const [h, m] = hhmm.split(':');
  return { hour: parseInt(h, 10) || 0, minute: parseInt(m, 10) || 0 };
};
